import { Crisp } from 'crisp-sdk-web';
import { PropsWithChildren, useContext, useEffect } from 'react';

import { useViewerRoles, useWorkspace } from '@treams/treams-hooks';

import AuthorizationContext from '../../contexts/AuthorizationContext';

const CRISP_WEBSITE_ID = 'e47a78ed-aa11-455a-842a-901ffda09cd5';

export default function HelpdeskContainer(props: PropsWithChildren<{}>) {
	const context = useContext(AuthorizationContext);
	const workspace = useWorkspace();
	const { loading, isWorkspaceSuperuser } = useViewerRoles();

	useEffect(() => {
		// Mirrors Crisp.load() so we control the injected tag's attributes.
		// Deliberately no `integrity`: l.js is an unversioned loader that pulls further
		// scripts from Crisp's CDN, so a pinned hash breaks on every vendor redeploy —
		// it did, and was reverted in #2284. The CSP `script-src` allowlist is the control.
		Crisp.configure(CRISP_WEBSITE_ID, { autoload: false });

		const w = window as any;
		if (w.$crisp?.is) {
			return;
		}

		w.CRISP_WEBSITE_ID = CRISP_WEBSITE_ID;
		w.CRISP_RUNTIME_CONFIG = {};

		Crisp.setSafeMode(true);

		const script = document.createElement('script');
		script.src = 'https://client.crisp.chat/l.js';
		script.async = true;
		script.crossOrigin = 'anonymous';
		document.head.appendChild(script);
	}, []);

	useEffect(() => {
		if (loading) {
			return;
		}

		if (context?.authenticatedUser) {
			const { signedEmail, firstName, lastName } = context.authenticatedUser;
			Crisp.user.setCompany(workspace.id, {});
			Crisp.user.setNickname(`${firstName} ${lastName}`);

			if (signedEmail) {
				Crisp.user.setEmail(signedEmail.email, signedEmail.hmac);
			}
		}
	}, [
		!!context,
		!!context?.authenticatedUser,
		context?.authenticatedUser.privacyConsent?.version,
		loading,
		isWorkspaceSuperuser,
	]);

	return <>{props.children}</>;
}
